import React, { Component } from 'react'
import PropTypes from 'prop-types'

import Context from '#context'

import { onChange } from '#helper/onChange'
import { attributesSorting } from '#helper/showData'
import { isTemperatureSensor } from '#helper/deviceTypes'

import Input from '#comp/Custom/Input'

import Select from './Select.jsx'

/**
 * @class
 * @classdesc - Display a Select component inside editable devices.
 * @example
 * <SingleAttribute input={input} inputs={inputs} />
 */
class SingleAttribute extends Component {
	static contextType = Context

	/**
	 * @typedef {Object} PropTypes
	 * @property {Object} input - The required input object.
	 * @property {Array} inputs - The required array of inputs.
	 * @property {Array} required - The array of required attributes.
	 */
	static propTypes = {
		input: PropTypes.object.isRequired,
		inputs: PropTypes.array.isRequired,
		required: PropTypes.array,
	}
	static defaultProps = {
		input: {},
		inputs: [],
		required: [],
	}

	isSelectInput = (name) => {
		return ['installation_place', 'installation_number'].includes(name)
	}

	getType = (input) => {
		if (
			input === 'offset_a' ||
			input === 'offset_b' ||
			input === 'pulse_weight_a' ||
			input === 'pulse_weight_b'
		) {
			return 'number'
		}
		return false
	}

	render() {
		const { updateParent, device } = this.context
		const { input, inputs, required } = this.props

		if (isTemperatureSensor(device) && this.isSelectInput(input.name)) {
			return <Select input={input} inputs={inputs} />
		}

		return (
			<Input
				name={input.name}
				onChange={(e) => {
					onChange(e, (keyValue) => updateParent(keyValue))
				}}
				value={input.value === null ? '' : input.value}
				required={required.includes(input.name)}
				type={this.getType(input.name)}
			>
				{input.displayname}
			</Input>
		)
	}
}

/**
 * @class
 * @classdesc - Handle the inputs when the edit mode is selected.
 * @example
 * <Inputs
 * appControlled={appControlled}
 * updateParent={this.updateInputs}
 * inputs={inputs}
 * devices={devices}
 * device={device}
 * />
 */
export default class Inputs extends Component {
	static contextType = Context

	/**
	 * @typedef {Object} PropTypes
	 * @property {Array} appControlled - An array of values controlled by the application.
	 * @property {Function} updateParent - A function to update the parent component or context.
	 * @property {Array} inputs - An array of input values.
	 * @property {Array} devices - An array of devices.
	 * @property {Object} device - An object representing a device.
	 * @property {Array} required - The array of required attributes.
	 */
	static propTypes = {
		appControlled: PropTypes.array.isRequired,
		updateParent: PropTypes.func.isRequired,
		inputs: PropTypes.array.isRequired,
		devices: PropTypes.array.isRequired,
		device: PropTypes.object.isRequired,
		required: PropTypes.array,
	}
	static defaultProps = {
		appControlled: [],
		updateParent: () => {},
		inputs: [],
		devices: [],
		device: {},
		required: [],
	}

	render() {
		const { t, temperatureThresholds } = this.context
		const {
			appControlled,
			updateParent,
			inputs,
			devices,
			device,
			required,
		} = this.props

		return (
			<Context.Provider
				value={{
					t,
					temperatureThresholds,
					updateParent,
					inputs,
					devices,
					device,
					appControlled,
				}}
			>
				<div className="flex flex-col space-y-4">
					{attributesSorting(inputs, t).map((input, i) => (
						<div key={i + '_Inputs'}>
							<SingleAttribute
								input={input}
								inputs={inputs}
								required={required}
							/>
						</div>
					))}
				</div>
			</Context.Provider>
		)
	}
}
